import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import {withStyles} from "@mui/styles";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import AddUserIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {ValidatorForm, TextValidator} from "react-material-ui-form-validator";
import {toast} from "react-toastify";
import spinner from "../assign-prescriptions/images/spinner.gif";
import {Divider} from "@mui/material";
import USER_ROLES from "../common/userroles";
import {withTranslation} from "react-i18next";
import enLocale from "date-fns/locale/en-US";
import frLocale from "date-fns/locale/fr";
import ROLES_TYPE from "../enum/role_type";

import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  foldFiltersToSearchText,
  getActionsColumn,
  openAgGridContextMenu,
  actionMenuItem,
} from "../grid/iLocalAGGrid";



class EnhancedTableHead extends React.Component {
  createSortHandler = (property, field) => (event) => {
    this.props.onRequestSort(event, property, field);
  };

  render() {
    const {onSelectAllClick, order, orderBy, numSelected, rowCount, sortId, t} = this.props;
    const rows = [
      {
        id: "name",
        numeric: false,
        disablePadding: true,
        label: t("Roles.column.name"),
        field: "name",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: t("Users.Actions"),
      },
    ];
    return (
      <TableHead sx={{ height: "56px" }}>
        <TableRow>
          <TableCell sx={{ padding: "0 12px" }}>
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onChange={onSelectAllClick}
              sx={{
                "&.Mui-checked": { color: "#0ae" },
                "&.MuiCheckbox-indeterminate": { color: "#0ae" },
              }}
            />
          </TableCell>
          {rows.map((row) => {
            return (
              <TableCell
                key={row.id}
                numeric={row.numeric}
                padding={"none"}
                sortDirection={orderBy === row.id ? order : false}
                sx={{
                  color: "rgba(0, 0, 0, 0.54)",
                  fontSize: "0.75rem",
                  fontWeight: "500",
                }}
              >
                <Tooltip
                  title={t("Global.Label.Filter.Sort")}
                  placement={row.numeric ? "bottom-end" : "bottom-start"}
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderBy === row.field && sortId === row.id}
                    direction={order}
                    onClick={this.createSortHandler(row.id, row.field)}
                    sx={{ ".MuiSvgIcon-root": { fontSize: 16 } }}
                  >
                    {row.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            );
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};
EnhancedTableHead = withTranslation()(EnhancedTableHead);

const toolbarStyles = (theme) => ({
  root: {
    paddingRight: theme.spacing.unit,
  },
  highlight:
    theme.palette.mode === "light"
      ? {
          color: "#4fc3f7",
          backgroundColor: "rgb(255, 234, 231)",
        }
      : {
          color: "#4fc3f7",
          backgroundColor: "rgb(255, 234, 231)",
        },
  spacer: {
    flex: "1 1 100%",
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  title: {
    flex: "0 0 auto",
  },
  add: {
    backgroundColor: "#00a7e1",
    color: "#fff",
    transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover": {
      backgroundColor: "#6cca98",
      color: "#fff",
    },
  },
  gridActions: {
    display: "inline-block",
    width: "144px",
  },
});


let EnhancedTableToolbar = (props) => {
  const {
    numSelected,
    classes,
    add,
    confirmDelete,
    uadd,
    udelete,
    t,
  } = props;

  return (
    <Toolbar
      className={classNames(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <div className={classes.title}>
        {numSelected > 0 ? (
          <Typography color="" variant="subheading">
            {numSelected} {t("Users.SelectedRowsCount")}
          </Typography>
        ) : null}
      </div>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        {numSelected === 1 ? (
          <div>
            {udelete === true ? (
              <Tooltip
                key="single-delete"
                title={t("Roles.DeleteRolesTooltip")}
              >
                <IconButton
                  aria-label="Delete"
                  onClick={confirmDelete}
                  sx={() => ({
                    "&:hover": { backgroundColor: "#6cca98", color: "#fff" },
                  })}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : (
              ""
            )}
          </div>
        ) : numSelected > 1 ? (
          udelete === true ? (
            <Tooltip key="multi-delete" title={t("Roles.DeleteRolesTooltip")}>
              <IconButton
                aria-label="Delete"
                onClick={confirmDelete}
                sx={() => ({
                  "&:hover": { backgroundColor: "#6cca98", color: "#fff" },
                })}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            ""
          )
        ) : uadd === true ? (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Tooltip key="single-add" title={t("Roles.AddRoleTooltip")}>
              <IconButton
                aria-label={t("Roles.AddRoleTooltip")}
                className={classes.add}
                onClick={add}
              >
                <AddUserIcon />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          ""
        )}
      </div>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
};

EnhancedTableToolbar = withStyles(toolbarStyles)(
  withTranslation()(EnhancedTableToolbar)
);

const styles = (theme) => ({
  Paper: {
    minWidth: "985px",
  },
  root: {
    width: "100%",
  },
  table: {
    minWidth: "auto",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  halfTextField: {
    marginRight: "20px",
  },
  dropbox: {
    width: "135px",
    height: "105px",
    float: "right",
    textAlign: "center",
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
    overflow: "hidden",
    top: "56px",
    right: "2px",
    position: "relative",
  },
  innerBox: {
    margin: "0 auto",
    display: "block",
    height: "100%",
  },
  dropImage: {
    width: "80%",
    height: "100%",
  },
  dropPic: {
    width: "100%",
    height: "100%",
  },
  errorDiv: {
    paddingTop: "10px",
    color: "red",
  },
  captionText: {
    lineHeight: "1",
    color: "#8a8a8a",
    fontSize: "13px",
    textAlign: "center",
  },
  twoChildren: {
    display: "flex",
    justifyContent: "space-between",
  },
  leftChild: {},
  rightChild: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  spinner: {
    position: "absolute",
    background: "rgba(255,255,255,.4)",
    width: "100%",
    height: "100%",
    zIndex: "9999",
    top: 0,
    left: 0,
  },
  spinnerImage: {
    top: "calc(50% - 32.5px)",
    left: "calc(50% - 65px)",
    position: "absolute",
    width: "130px",
    userSelect: "none",
  },
  hidden: {
    display: "none",
  },
  formControl: {
    margin: "5px",
    minWidth: "100%",
  },
  boxDesign: {
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButton: {
    background: "#6cca98",
    color: "#fff",
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
  dialogTitle: {
    fontSize: "1.3125rem",
    fontWeight: 500,
    lineHeight: "1.16667em",
  },
  menuItem: {
    paddingTop: "10px",
    paddingBottom: "10px",
  },
});

let FilterObj = {
  searchtext: "",
  searchObject: null,
  pageNo: 0,
  pageSize:  10,
  sortOrder: "desc",
  sortColumn: "created_on",
  tableName: "roles",
};

const RoleSortObj = {
  name: { tableName: "roles", sortColumn: "name" },
};

class RoleManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      order: "desc",
      orderBy: "created_on",
      selected: [],
      page: 0,
      rowsPerPage: 10,
      submitted: false,
      isButtonDisabled: false,
      userview: true,
      useradd: true,
      userdelete: true,
      useredit: true,
      searchtext: "",
      loader: false,
      isDisabled: false,
      innerLoader: false,
      rowID: null,
      isSuperAdmin: false,
      isAdmin: false,
      totalRecords: 0,
      locale: localStorage.getItem("i18nextLng") === "en" ? enLocale : frLocale,
      boxList: [],
      role_name: "",
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.setState({
      isSuperAdmin:
        sessionStorage.hasOwnProperty("superadmin") &&
        JSON.parse(sessionStorage.getItem("superadmin")) ===
          USER_ROLES.SUPERADMIN,
      isAdmin:
        sessionStorage.hasOwnProperty("admin") &&
        JSON.parse(sessionStorage.getItem("admin")) === USER_ROLES.ADMIN,
    });
    this.setPermissions();
  }

  refreshGrid = () => {
    if (!this.gridApi) return;
    if (typeof this.gridApi.refreshServerSideStore === "function") {
      this.gridApi.refreshServerSideStore({ purge: true });
    } else if (typeof this.gridApi.refreshServerSide === "function") {
      this.gridApi.refreshServerSide({ purge: true });
    }
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    const datasource = createServerSideDatasource({
      filterBody: FilterObj,
      applyFilterModel: (body, filterModel) => {
        body.searchtext = foldFiltersToSearchText(filterModel);
        body.searchObject =
          filterModel && Object.keys(filterModel).length ? filterModel : null;
      },
      sortMap: RoleSortObj,
      pageZeroBased: true,
      getPageSize: () => FilterObj.pageSize || 10,
      fetchPage: (body) =>
        fetch(process.env.REACT_APP_BASEURL + "role-management/list", {
          method: "post",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((data) => ({ results: (data && data.data && data.data.results) || [], total: (data && data.data && data.data.total) || 0 })),
      onSuccess: (results, total) => {
        this.setState({
          data: results,
          totalRecords: total,
          loader: false,
          isDataEmpty: !results || results.length === 0,
          selected: [],
        });
      },
      onError: () => this.setState({ loader: false }),
    });
    if (typeof params.api.setServerSideDatasource === "function") {
      params.api.setServerSideDatasource(datasource);
    }
  };

  onRowSelected = (params) => {
    if (!params || !params.api) return;
    const selectedNodes = params.api.getSelectedNodes() || [];
    this.setState({
      selected: selectedNodes.map((n) => n.data && n.data.id).filter(Boolean),
    });
  };

  onPaginationChanged = (params) => {
    if (!params.api) return;
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && FilterObj.pageSize !== newPageSize) {
      FilterObj.pageSize = newPageSize;
    }
  };

  BindBoxes() {
    if (this.gridApi) {
      this.refreshGrid();
      return;
    }
    this.setState({ loader: true });
    fetch(process.env.REACT_APP_BASEURL + "role-management/list", {
      method: "post",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(FilterObj),
    })
      .then((response) => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then((data) => {
        console.log(data);
        this.setState({ totalRecords: data && data.data && data.data.total });
        if (
          data &&
          data.data &&
          data.data.results &&
          data.data.results.length > 0
        ) {
          const filteredResults = data.data.results.filter(
            item => 
              item.id !== ROLES_TYPE.RetailAdmin &&
              item.id !== ROLES_TYPE.RetailTechnician
          );
          this.setState({
            data: [true, 'true'].includes(this.state.isSuperAdmin) ? data.data.results : filteredResults,
            loader: false,
            isDataEmpty: false,
          });
        } else {
          this.setState({ data: null, loader: false, isDataEmpty: true });
        }
      });
  }

  gettingOnSortTableName = (field) => {
    let tablename = "";
    if (["name"].includes(field)) {
      tablename = "roles";
    }
    return tablename;
  };

  handleRequestSort = (event, property, field) => {
    const orderBy = property;
    let order = "desc";
    if (this.state.order === "desc") {
      order = "asc";
    }
    this.setState({ order, orderBy, sortId: property });
    FilterObj.sortOrder = order;
    FilterObj.sortColumn = field;
    FilterObj.tableName = this.gettingOnSortTableName(orderBy);
    
    if (this.state.data && this.state.data.length > 0) {
      this.BindBoxes();
    }
  };

  handleSelectAllClick = (event) => {
    if (event.target.checked) {
      this.setState((state) => ({ selected: state.data.map((n) => n.id) }));
      return;
    }
    this.setState({ selected: [] });
  };

  handleClick = (event, id) => {
    const {selected} = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    this.setState({ selected: newSelected });
  };

  handleChangePage = (event, page) => {
    event && event.preventDefault();
    
    FilterObj.pageNo = page;
    if (this.state.data && this.state.data.length > 0) {
      this.BindBoxes();
    }
  };

  handleChangeRowsPerPage = (event) => {
    
    FilterObj.pageNo = 0;
    FilterObj.pageSize = event.target.value;
    if (this.state.data && this.state.data.length > 0) {
      this.BindBoxes();
    }
  };

  addUser = (event) => {
    this.setState({
      showNewUserDialog: true,
      operation: "Add",
      role_name: "",
    });
  };

  handleClose = (event) => {
    this.setState({ showNewUserDialog: false, user: {}, errorMessage: "" });
  };

  handleChange = (event) => {
    if (event.preventDefault) event.preventDefault();
    this.setState({ role_name: event.target.value }, () => {
      console.log(this.state && this.state.role_name);
    });
  };

  handleView = (event) => {
    const selectedObject = this.state.data.find(
      (x) => x.id === this.state.selectedRowId
    );
    this.setState({
      showNewUserDialog: true,
      operation: "View",
      rowID: selectedObject && selectedObject.id,
      role_name: selectedObject.name,
    });
    this.handleContextClose(event);
  };

  handleEdit = (event) => {
    const selectedObject = this.state.data.find(
      (x) => x.id === this.state.selectedRowId
    );
    this.setState({
      showNewUserDialog: true,
      operation: "Edit",
      role_name: selectedObject.name,
      selectedRowId: selectedObject && selectedObject.id,
      data: this.state && this.state.data,
    });
    this.handleContextClose(event);
  };

  confirmDelete = (event) => {
    // this.forDeletion
    if (this.state.selectedRowId) {
      this.setState({
        deleteDialogOpen: true,
        forDeletion: this.state.data.filter(
          (x) => x.id === this.state.selectedRowId
        ),
      });
    } else {
      this.setState({
        deleteDialogOpen: true,
        forDeletion: this.state.data.filter(
          (x) => this.state.selected.indexOf(x.id) > -1
        ),
      });
    }
  };

  deleteConfirmClose = (event) => {
    if (this.state.selectedRowId) {
      this.setState({
        deleteDialogOpen: false,
        selectedRowId: null,
        forDeletion: null,
      });
      this.handleContextClose(event);
    } else {
      this.setState({
        deleteDialogOpen: false,
        selectedRowId: null,
        forDeletion: null,
      });
    }
  };

  deleteConfirmed = (event) => {
    this.setState({ deleteDialogOpen: false });
    this.handleDelete(event);
  };

  handleDelete = (event) => {
    event && event.preventDefault();
    const {selected, selectedRowId} = this.state;
    this.setState({ loader: true });
    let IdsToDelete = [];
    if ([undefined, "undefined", null, "null", ""].includes(selectedRowId)) {
      IdsToDelete.push(selected);
    } else {
      IdsToDelete.push(selectedRowId);
    }
    const result = fetch(
      process.env.REACT_APP_BASEURL +
        "role-management?id=" +
        IdsToDelete.join(",").trim(","),
      {
        method: "DELETE",
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
        },
      }
    );
    result.then((_result) => {
      _result.json().then((res) => {
        this.setState({ loader: false });
        this.handleContextClose(event);
        this.BindBoxes();
        this.setState({
          isButtonDisabled: false,
          submitted: true,
          showNewUserDialog: false,
          errorMessage: "",
          searchtext: "",
          selectedRowId: "",
        });
        if ([200, "200"].includes(res && res.status)) {
          toast.success(
            this.props.t("Roles.Operation.RoleDeletedSuccessfully")
          );
        } else {
          toast.error(this.props.t("Roles.Operation.RoleDeletionFailed"));
        }
      });
    });
  };

  handleSearchChange = (event) => {
    let searchtxt = "";
    searchtxt = event.target.value;
    this.setState({ searchtext: event.target.value });
    FilterObj.sortOrder = "desc";
    FilterObj.sortColumn = "created_on";
    FilterObj.tableName = "";
    if (searchtxt.length >= 3) {
      FilterObj.searchtext = searchtxt;
      this.BindBoxes();
    } else if (searchtxt.length === 0) {
      FilterObj.searchtext = "";
      this.BindBoxes();
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState({
      loader: true,
      isButtonDisabled: true,
    });
    var data = {
      name: this.state && this.state.role_name,
    };
    const result = fetch(process.env.REACT_APP_BASEURL + "role-management", {
      method: "POST",
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    result
      .then((result) => {
        result.json().then((res) => {
          this.setState({ loader: false });
          if ([201, "201"].includes(res && res.status)) {
            toast.success(
              this.props.t("Roles.Operation.RoleAddedSuccessfully")
            );
            FilterObj.searchtext = "";
            FilterObj.sortOrder = "desc";
            FilterObj.sortColumn = "created_on";
            FilterObj.tableName = "";
            this.BindBoxes();
            this.setState({
              isButtonDisabled: false,
              submitted: true,
              showNewUserDialog: false,
              errorMessage: "",
              searchtext: "",
              selectedRowId: "",
            });
          } else {
            toast.error(res && res.message);
            this.setState({
              isButtonDisabled: false,
              submitted: false,
            });
          }
        });
      })
      .catch((error) => {
        this.setState({ loader: false });
        this.setState({ errorMessage: error.message, isButtonDisabled: false });
      });
  };

  handleUpdate = (event) => {
    event.preventDefault();
    this.setState({
      loader: true,
      isButtonDisabled: true,
    });
    var payload = {
      name: this.state && this.state.role_name,
    };
    console.log({ rowId: this.state.selectedRowId });
    const selectedObject = this.state.data.find(
      (x) => x.id === this.state.selectedRowId
    );
    const result = fetch(
      process.env.REACT_APP_BASEURL + "role-management?id=" + selectedObject.id,
      {
        method: "PUT",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
    result
      .then((result) => {
        result.json().then((res) => {
          this.setState({ loader: false });
          if ([200, "200"].includes(res && res.status)) {
            toast.success(
              this.props.t("Roles.Operation.RoleUpdatedSuccessfully")
            );
            this.setState({
              isButtonDisabled: false,
              submitted: true,
              showNewUserDialog: false,
              searchtext: "",
              selectedRowId: "",
            });
            FilterObj.searchtext = "";
            FilterObj.sortOrder = "desc";
            FilterObj.sortColumn = "created_on";
            FilterObj.tableName = "";
            this.BindBoxes();
          } else {
            this.setState({
              isButtonDisabled: false,
              submitted: false,
            });
            toast.error(res && res.message);
          }
        });
      })
      .catch((error) => {
        this.setState({ loader: false });
        this.setState({ errorMessage: error.message });
      });
  };

  isSelected = (id) => this.state.selected.indexOf(id) !== -1;

  handleContextClick = (event, n, onReady) => {
    if (event && event.preventDefault) event.preventDefault();
    const selectedRowId = n && n.id;
    // Avoid setState when opening ⋮ menu — full page re-render causes flicker.
    if (typeof onReady === 'function') {
      // Intentional: skip setState so opening ⋮ does not re-render the page.
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state.selectedRowId = selectedRowId;
      onReady();
      return;
    }
    this.setState({ selectedRowId });
  };

  handleContextClose = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState({ selectedRowId: null });
  };

  openRowActionsMenu = (event, params, menuHelpers) => {
    const openMenu = () => {
      const items = this.getContextMenuItems({
        ...params,
        column: params.column,
        node: params.node,
        value: params.value,
      });
      if (menuHelpers && typeof menuHelpers.openMenu === 'function') {
        menuHelpers.openMenu(items);
        return;
      }
      openAgGridContextMenu(params, (menuHelpers && menuHelpers.positionEvent) || event);
    };
    this.handleContextClick(event, params && params.data, openMenu);
  };

  getContextMenuItems = (params) => {
    const colId = params && params.column && (params.column.getColId ? params.column.getColId() : params.column.colId);
    if (colId !== 'actions') return [];
    const {t} = this.props;
    if (!(params.node && params.node.data)) return [];
    const items = [];
    if (this.state.userview === true) {
      items.push(actionMenuItem({ name: t('Users.ViewActions'), icon: 'view', action: () => this.handleView() }));
    }
    if (this.state.useredit === true) {
      items.push(actionMenuItem({ name: t('Users.EditActions'), icon: 'edit', action: () => this.handleEdit() }));
    }
    if (this.state.userdelete === true) {
      items.push(actionMenuItem({
        name: t('Users.DeleteActions'),
        icon: 'delete',
        disabled: this.state.isDisabled === true,
        action: () => this.confirmDelete(),
      }));
    }
    return items;
  };

  setPermissions = () => {
    let permissions = JSON.parse(sessionStorage.getItem("permissions"));
    let _checkUserRole = JSON.parse(sessionStorage.getItem("permissionUser"));
    if (_checkUserRole === USER_ROLES.SUPERADMIN) {
      this.setState({
        userview: true,
        useradd: true,
        useredit: true,
        userdelete: true,
      });
    } else {
      if (permissions && permissions.length > 0) {
        const _manageUsers = permissions.filter((item) => {
          return item.permission_entity_type === "Manage Roles";
        });
        if (_manageUsers.length > 0) {
          this.setState({
            useradd: _manageUsers.some((x) => x.name.trim() === "Add" && x.is_allowed === 1),
            useredit: _manageUsers.some((x) => x.name.trim() === "Modify" && x.is_allowed === 1),
            userview: _manageUsers.some((x) => x.name.trim() === "View" && x.is_allowed === 1),
            userdelete: _manageUsers.some((x) => x.name.trim() === "Delete" && x.is_allowed === 1),
          });
        } else {
          this.setState({
            userview: false,
            useradd: false,
            useredit: false,
            userdelete: false,
          });
        }
      } else {
        this.setState({
          userview: false,
          useradd: false,
          useredit: false,
          userdelete: false,
        });
      }
    }
  };

  render() {
    const {classes, t} = this.props;
    const {selected} = this.state;
    const readOnly = this.state.operation === "View";
    return (
      <div className={classes.root}>
        {this.state.loader ? (
          <div className={classes.spinner}>
            <img className={classes.spinnerImage} src={spinner} alt="loading" />
          </div>
        ) : null}
        <Dialog
          open={this.state.deleteDialogOpen || false}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t("Users.AreYouSureDeleteText")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("Role.AreYouSureDeleteBodyText")} :
              <ul>
                {this.state.forDeletion &&
                  this.state.forDeletion.map(function(a) {
                    return <li>{a.name}</li>;
                  })}
              </ul>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.deleteConfirmClose}
              color="primary"
              className={classes.primaryButton}
            >
              {t("Users.AreYouSureDeleteCancelButton")}
            </Button>
            <Button
              onClick={this.deleteConfirmed}
              color="primary"
              autoFocus
              className={classes.primaryButton}
            >
              {t("Users.AreYouSureDeleteButton")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          maxWidth="md"
          open={this.state.showNewUserDialog || false}
          onClose={this.handleClose}
          aria-labelledby="form-dialog-title"
          classes={{ paper: classes.Paper }}
        >
          {this.state.loader ? (
            <div className={classes.spinner}>
              <img
                className={classes.spinnerImage}
                src={spinner}
                alt="loading"
              />
            </div>
          ) : null}
          <div className={classes.paddedBoxDivEdit}>
            <ValidatorForm
              ref="form"
              onSubmit={
                this.state.operation === "Add"
                  ? this.handleSubmit
                  : this.handleUpdate
              }
            >
              <DialogTitle id="form-dialog-title">
                {t(this.state.operation)} {t("Roles.Operation.Title")}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <span className={classes.errorDiv}>
                    {this.state.errorMessage}
                  </span>
                </DialogContentText>
                <div className={classes.twoChildren}>
                  <div className={classes.leftChild}>
                    <div style={{ display: "flex" }}>
                      <TextValidator
                        autoFocus
                        className={classes.halfTextField}
                        margin="normal"
                        value={this.state && this.state.role_name}
                        id="role_name"
                        inputProps={{
                          readOnly: Boolean(readOnly),
                          maxLength: 50,
                          autocomplete: "off",
                        }}
                        label={t("Roles.column.name")}
                        name="Role Name"
                        type="role_name"
                        placeholder=""
                        style={{ width: "350px" }}
                        onChange={this.handleChange}
                        validators={["required"]}
                        errorMessages={[t("Roles.RoleNameValidation")]}
                        variant="standard"
                      />
                    </div>
                  </div>
                </div>
              </DialogContent>
              <div style={{ bottom: "0", width: "100%" }}>
                <DialogActions sx={{ padding: "15px 12px 9px 4px" }}>
                  <Button
                    onClick={this.handleClose}
                    color="primary"
                    className={classes.primaryButton}
                  >
                    {this.state.operation === "View"
                      ? t("Users.CloseButton")
                      : t("Users.CancelButton")}
                  </Button>
                  {this.state.operation !== "View" && (
                    <Button
                      type="submit"
                      disabled={this.state.isButtonDisabled}
                      color="primary"
                      className={classes.primaryButton}
                    >
                      {this.state.operation === "Edit"
                        ? t("Role.Action.Button.Update")
                        : t("Role.Action.Button.Add")}
                    </Button>
                  )}
                </DialogActions>
              </div>
            </ValidatorForm>
          </div>
        </Dialog>
        <EnhancedTableToolbar
          numSelected={selected.length}
          add={this.addUser}
          deleteUsers={this.deleteUsers}
          editUser={this.editUser}
          confirmDelete={this.confirmDelete}
          uadd={this.state.useradd}
          udelete={this.state.userdelete}
        />
        <div className={classes.tableWrapper}>
          <Divider
            style={{
              marginTop: "10px",
              height: "5px",
              borderColor: "rgba(0, 0, 0, 0.12)",
              borderStyle: "solid",
              borderBottomWidth: "5px",
            }}
          />
                    <ILocalAGGrid
            columnDefs={[
              { field: "name", headerName: t("Roles.column.name") },
              getActionsColumn({
                headerName: t("Users.Actions"),
                onActionsClick: (event, params, menuHelpers) => this.openRowActionsMenu(event, params, menuHelpers),
              }),
            ]}
            defaultColDef={getDefaultColDef()}
            rowSelection="multiple"
            rowModelType="serverSide"
            cacheBlockSize={FilterObj.pageSize || 10}
            paginationPageSize={FilterObj.pageSize || 10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={45}
            getRowId={(params) => params.data && params.data.id}
            onGridReady={this.onGridReady}
            onRowSelected={this.onRowSelected}
            onPaginationChanged={this.onPaginationChanged}
            getContextMenuItems={this.getContextMenuItems}
          />
        </div>
      </div>
    );
  }
}

RoleManagement.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(withTranslation()(RoleManagement));



// WEBPACK FOOTER //
// ./src/components/role-management/index.js